import { DATE_FORMAT } from '@/helpers/dates';
import { uploadDocumentsWithToast } from '@/helpers/uploadDocumentsWithToast';
import { useGetAccountQuery } from '@/services/accounts';
import { useSaveActivationPackageMutation } from '@/services/activationPackage';
import { useGetDocumentsQuery, useUploadDocumentMutation } from '@/services/documents';
import { isErrorWithData } from '@/services/helpers';
import { useSaveInstallEnergyBackupPackageMutation } from '@/services/installEnergyBackupPackage';
import { useSaveInstallGeneratorPackageMutation } from '@/services/installGeneratorPackage';
import { useSaveInstallGeothermalPackageMutation } from '@/services/installGeothermalPackage';
import { useSaveInstallHVACPackageMutation } from '@/services/installHVACPackage';
import { useSaveInstallPackageMutation } from '@/services/installPackage';
import { useSaveInstallWaterHeaterPackageMutation } from '@/services/installWaterHeaterPackage';
import { useSavePostActivationPackageMutation } from '@/services/postActivationPackage';
import { useGetQueueByAccountIdAndTypeQuery, useUpdateFlagMutation } from '@/services/queues';
import { useGetRequirementFlagSettingsQuery } from '@/services/requirementFlagSettings';
import {
  Badge,
  Box,
  DateInput,
  FormikTextInput,
  SelectInputInset,
  toast,
  useBreakpoint,
  useOpenClose,
} from '@palmetto/palmetto-components';
import { Field, FormikValues } from 'formik';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
// Package-saving imports
import { useIntercom } from 'react-use-intercom';

import {
  ActivationPackage,
  DeepPartial,
  DocumentSortField,
  Flag,
  FlagActionHistory,
  FlagDocumentTypes,
  FlagHistoryStatus,
  FlagPurposeType,
  FlagStatus,
  FlagStatusLabels,
  FlagSubject,
  FlagTypeLabels,
  flagTypesHaveNoAction,
  flagTypesNeedingFormField,
  InstallEnergyBackupPackage,
  InstallGeothermalPackage,
  InstallHVACPackage,
  InstallPackage,
  InstallGeneratorPackage,
  InstallWaterHeaterPackage,
  MilestonePackageRoute,
  MilestonePackageRouteToLabels,
  MilestoneRouteToQueueTypeMap,
  PACKAGE_RESTRICTED_ACTIONS_QUEUE_STATUSES,
  PostActivationPackage,
  ProgramType,
  QueueStatus,
  QueueType,
  RequirementFlagSettingsDocument,
  RequirementFlagSettingsType,
} from 'types';

import { ReviewPackageType } from '../../types/ReviewPackage';
import { formatActivationPackageData } from '../ActivationMilestonePackage/ActivationPackage';
import { useAuth } from '../auth/authProvider';
import { isPalmettoFinanceUser } from '../auth/RequirePalmettoFinanceUser';
import DateTimeDisplay from '../DateTime';
import { formatInstallPackageData as formatInstallEnergyBackupPackageData } from '../InstallMilestonePackage/InstallEnergyBackupMilestonePackage';
import { formatInstallGeneratorPackagesData } from '../InstallMilestonePackage/InstallGeneratorPackage';
import { formatInstallGeothermalPackageData } from '../InstallMilestonePackage/InstallGeothermalPackage';
import { formatInstallHVACPackagesData } from '../InstallMilestonePackage/InstallHVACPackage';
import { formatInstallPackagesData } from '../InstallMilestonePackage/InstallPackage';
import { formatInstallWaterHeaterPackagesData } from '../InstallMilestonePackage/InstallWaterHeaterPackage';
import { MainContainer } from '../MainContainer';
import PageHeader from '../PageHeader';
import { formatPostActivationPackageData } from '../PostActivationMilestonePackage/PostActivationPackage';
import { QueueFlagsProvider } from '../QueueFlags/QueueFlagsContext';
import { NewAddFlagModal } from '../QueueFlags/RevampAddFlagModal';

import { FlagActionTimeline } from './components/FlagActionTimeline';
import { FlagInteractionControls } from './components/FlagInteractionControls';
import { FlagStatusDisplay } from './components/FlagStatusDisplay';
import { ReviewerQCSection } from './components/ReviewerQCSection';
import { ReviewPackageForm } from './components/ReviewPackageForm';
// Extracted sub-components
import { useFlagPageState } from './hooks/useFlagPageState';
import { TasksFooter } from './TasksFooter';

type SavePackageType = DeepPartial<
  | InstallHVACPackage
  | InstallPackage
  | ActivationPackage
  | PostActivationPackage
  | InstallEnergyBackupPackage
  | InstallWaterHeaterPackage
  | InstallGeneratorPackage
  | InstallGeothermalPackage
> & { bypassApprovalValidation?: boolean };

enum SpecialFlagSections {
  PVSystem = 'PV System',
  YearOneProduction = 'Year-1 Production Estimate',
  SystemDesignIssue = 'System Design Issue',
  SystemCostDiscrepancyHVAC = 'System Cost Discrepancy HVAC',
  ElectricalUpgrade = 'Electrical Upgrade',
  EnergyBackupExistingSolarSystem = 'Energy Backup Existing Solar System',
  SystemCostDiscrepancyWaterHeater = 'System Cost Discrepancy Water Heater',
  SystemCostDiscrepancyGeothermal = 'System Cost Discrepancy Geothermal',
}

const reviewPackageSaveHandling = {
  [ReviewPackageType.installationHVAC]: {
    format: formatInstallHVACPackagesData,
    useMutation: useSaveInstallHVACPackageMutation,
  },
  [ReviewPackageType.installation]: {
    format: formatInstallPackagesData,
    useMutation: useSaveInstallPackageMutation,
  },
  [ReviewPackageType.activation]: {
    format: formatActivationPackageData,
    useMutation: useSaveActivationPackageMutation,
  },
  [ReviewPackageType.postActivation]: {
    format: formatPostActivationPackageData,
    useMutation: useSavePostActivationPackageMutation,
  },
  [ReviewPackageType.installationEnergyBackup]: {
    format: formatInstallEnergyBackupPackageData,
    useMutation: useSaveInstallEnergyBackupPackageMutation,
  },
  [ReviewPackageType.installationWaterHeater]: {
    format: formatInstallWaterHeaterPackagesData,
    useMutation: useSaveInstallWaterHeaterPackageMutation,
  },
  [ReviewPackageType.installationGenerator]: {
    format: formatInstallGeneratorPackagesData,
    useMutation: useSaveInstallGeneratorPackageMutation,
  },
  [ReviewPackageType.installationGeothermal]: {
    format: formatInstallGeothermalPackageData,
    useMutation: useSaveInstallGeothermalPackageMutation,
  },
};

export const FlagDetailsPage = () => {
  const { id, milestonePackage, flagId, from, action } = useParams<{
    id: string;
    milestonePackage: MilestonePackageRoute;
    flagId: string;
    from?: string;
    action?: string;
  }>();
  const { claims } = useAuth();
  const { isOpen, handleOpen, handleClose } = useOpenClose();

  const isInitialRender = useRef(true);
  const { data: account, isLoading: isAccountLoading } = useGetAccountQuery(id as string, { skip: !id });
  const programType = account?.programType;
  const queueType = MilestoneRouteToQueueTypeMap[milestonePackage as MilestonePackageRoute];
  const { data: queue, isLoading: isQueueLoading } = useGetQueueByAccountIdAndTypeQuery(
    {
      accountId: account?.id,
      type: queueType,
    },
    {
      skip: !account?.id || !queueType,
    },
  );

  const [uploadDocument, { isLoading: isUploadingDocument }] = useUploadDocumentMutation();
  const [initialValue, setInitialValue] = useState<string | undefined>();

  const reviewPackageSaveActions: any = () => {
    let reviewPackageType: ReviewPackageType | undefined;

    switch (queue?.type) {
      case QueueType.activationPackage:
        reviewPackageType = ReviewPackageType.activation;
        break;
      case QueueType.postActivationPackage:
        reviewPackageType = ReviewPackageType.postActivation;
        break;
      default: {
        if (programType === ProgramType.hvac) {
          reviewPackageType = ReviewPackageType.installationHVAC;
        } else if (programType === ProgramType.waterHeater) {
          reviewPackageType = ReviewPackageType.installationWaterHeater;
        } else if (programType === ProgramType.generator) {
          reviewPackageType = ReviewPackageType.installationGenerator;
        } else if (programType === ProgramType.energyBackup) {
          reviewPackageType = ReviewPackageType.installationEnergyBackup;
        } else if (programType === ProgramType.geothermal) {
          reviewPackageType = ReviewPackageType.installationGeothermal;
        } else {
          reviewPackageType = ReviewPackageType.installation;
        }
        break;
      }
    }
    const [triggerSave] = reviewPackageSaveHandling[reviewPackageType].useMutation();
    return {
      triggerReviewPackageSave: triggerSave,
      formatReviewData: reviewPackageSaveHandling[reviewPackageType].format,
    };
  };
  const { formatReviewData, triggerReviewPackageSave } = reviewPackageSaveActions();

  const [updateCurrentFlag, { isLoading }] = useUpdateFlagMutation();

  const { isPhone } = useBreakpoint();
  const navigate = useNavigate();
  const [visitedFlags, setVisitedFlags] = useState<Set<string>>(new Set());

  const flag = queue?.flags?.find((f: Flag) => f?.id?.toString() === flagId);
  const flagIsClosed = flag?.status !== FlagStatus.open;

  const isReviewer = isPalmettoFinanceUser(claims);
  const documentType = flag?.type || flag?.documentType;
  const epcFeedbackDocumentType = FlagDocumentTypes.epcFeedback;
  const flagLabel = FlagTypeLabels[flag?.flagType as RequirementFlagSettingsType] || 'Unknown Flag';

  // Custom hook for flag page state
  const {
    selectedFlagStatus,
    setSelectedFlagStatus,
    newNote,
    setNewNote,
    changesOnFlag,
    setChangesOnFlag,
    replacementFileUpload,
    setReplacementFileUpload,
    epcFeedbackFileUpload,
    setEpcFeedbackFileUpload,
    replacementValue,
    setReplacementValue,
    flagFiles,
    setFlagFiles,
    flagIndexKey,
    setFlagIndexKey,
    sortedActionHistory,
    changesMadeOnFlag,
    badgeTextValuesAccordingToChangesType,
    saveFilesForFutureUpload,
  } = useFlagPageState(flag);

  const { data: documents = [], isLoading: isDocumentsLoading } = useGetDocumentsQuery(
    {
      accountId: id as string,
      documentTypeFilter: [documentType, epcFeedbackDocumentType],
      sort: DocumentSortField.ID_DESC,
    },
    {
      skip: !id,
    },
  );
  const { data: requirementFlagSettings } = useGetRequirementFlagSettingsQuery(
    { type: flag?.flagType },
    { skip: !flag?.flagType },
  );

  const setting: RequirementFlagSettingsDocument | undefined = requirementFlagSettings?.requirementFlagSettings?.[0];

  const hasNoAction = flagTypesHaveNoAction.includes(flag?.flagType);
  const needsFormField = flagTypesNeedingFormField.includes(flag?.flagType);

  const textInputDataProperty:
    | { field: string; value: any; component?: (props: any) => React.JSX.Element }
    | undefined = useMemo(() => {
    if (!account) {
      return undefined;
    }
    switch (flag?.flagType) {
      case RequirementFlagSettingsType.batteryMonitoringId:
        return { field: 'batteryMonitoringSiteId', value: account.batteryMonitoringSiteId };
      case RequirementFlagSettingsType.inverterMonitoringId:
        return { field: 'monitoringSiteId', value: account.monitoringSiteId };
      case RequirementFlagSettingsType.serialNumber:
        return { field: 'solarModuleSerialNumber', value: account.solarModuleSerialNumber };
      case RequirementFlagSettingsType.systemCostDiscrepancyHVAC: {
        return { field: 'workOrderTotalPrice', value: account.workOrderTotalPrice };
      }
      case RequirementFlagSettingsType.ahriNumber: {
        const splitWords = flag.dataKey.split(' ');
        const systemIndex = splitWords[splitWords.length - 1] - 1;
        return {
          field: 'ahriNumber',
          value: account.systemDesign.systems[systemIndex]?.ahriNumber,
          component: () => (
            <Box direction="row" gap="sm" alignItems="center">
              <Box fontSize="md">{`${flagLabel}:`}</Box>
              <Field
                type="text"
                name={`systems.${systemIndex}.ahriNumber`}
                id={`systems.${systemIndex}.ahriNumber`}
                width="2xl"
                autoComplete="off"
                component={FormikTextInput}
                isRequired
              />
            </Box>
          ),
        };
      }
      case RequirementFlagSettingsType.systemInstallDateHVAC: {
        const minDate = DateTime.fromISO('2022-01-01').toJSDate();
        const maxDate = DateTime.local().endOf('day').toJSDate();
        const installDate = DateTime.fromISO(account.systemInstallDate.palmetto).toUTC().toFormat(DATE_FORMAT);
        return {
          field: 'systemInstallDate',
          value: installDate,
          component: ({ setFieldValue, values }: any) => (
            <Box direction="row" gap="sm" alignItems="center">
              <Box fontSize="md">{`${flagLabel}:`}</Box>
              <Field
                label=""
                name="systemInstallDate"
                id="systemInstallDate"
                dateFormat={DATE_FORMAT}
                width="2xl"
                component={DateInput}
                value={values.systemInstallDate}
                datePickerProps={{
                  maxDate,
                  minDate,
                  onChange: (date: Date | [Date, Date] | null) => {
                    if (!date || !(date instanceof Date)) return;
                    setFieldValue('systemInstallDate', DateTime.fromJSDate(date).toUTC().toFormat(DATE_FORMAT));
                  },
                  openToDate: account.systemInstallDate
                    ? DateTime.fromFormat(installDate, DATE_FORMAT).toJSDate()
                    : undefined,
                }}
              />
            </Box>
          ),
        };
      }
      case RequirementFlagSettingsType.systemMonitoringId: {
        const splitWords = flag.dataKey.split(' ');
        const systemIndex = splitWords[splitWords.length - 1] - 1;
        return {
          field: 'monitoringId',
          value: account.systemDesign.systems[systemIndex]?.monitoringId,
          component: () => (
            <Box direction="row" gap="sm" alignItems="center">
              <Box fontSize="md">{`${flagLabel}:`}</Box>
              <Field
                type="text"
                name={`systems.${systemIndex}.monitoringId`}
                id={`systems.${systemIndex}.monitoringId`}
                width="2xl"
                autoComplete="off"
                component={FormikTextInput}
                isRequired
              />
            </Box>
          ),
        };
      }
      case RequirementFlagSettingsType.communityName:
        return { field: 'communityName', value: account.communityName };
      default:
        return undefined;
    }
  }, [flag, account, flagLabel]);

  const initialValues = useMemo(() => {
    if (!account || !textInputDataProperty) {
      return {};
    }

    if (isInitialRender.current && setInitialValue) {
      setInitialValue(textInputDataProperty.value);
      isInitialRender.current = false;
    }

    switch (flag?.flagType) {
      case RequirementFlagSettingsType.systemCostDiscrepancyHVAC:
      case RequirementFlagSettingsType.ahriNumber:
      case RequirementFlagSettingsType.systemMonitoringId: {
        return {
          systems: account?.systemDesign?.systems || [],
        };
      }
      case RequirementFlagSettingsType.systemInstallDateHVAC: {
        return {
          systems: account?.systemDesign?.systems || [],
          systemInstallDate: DateTime.fromISO(account.systemInstallDate.palmetto).toUTC().toFormat(DATE_FORMAT),
        };
      }
      case RequirementFlagSettingsType.batteryMonitoringId: {
        if (programType === ProgramType.energyBackup) {
          return {
            systemDesign: account?.systemDesign || {},
            ...(textInputDataProperty && { [textInputDataProperty.field]: textInputDataProperty.value }),
          };
        } else {
          return textInputDataProperty ? { [textInputDataProperty.field]: textInputDataProperty.value } : {};
        }
      }
      default: {
        if (textInputDataProperty) {
          return { [textInputDataProperty.field]: textInputDataProperty.value };
        }
        return {};
      }
    }
  }, [textInputDataProperty, account, flag, setInitialValue]);

  const documentIdFromDataKey = flag?.dataKey?.split('-')[0];
  const fileIndexFromDataKey = Number(flag?.dataKey?.split('-')[1]);
  const flaggedDocument = documents.find((doc: any) => doc.id === documentIdFromDataKey);
  const selectedFile = flaggedDocument?.files?.[fileIndexFromDataKey];
  const flaggedDocumentWithSingleFile = selectedFile ? { ...flaggedDocument, files: [selectedFile] } : null;

  const queueStatusIsApprovedOrConditionallyApproved: boolean =
    queue && (queue.status === QueueStatus.conditionallyApproved || queue.status === QueueStatus.approved);

  const canDeleteFlag =
    flag?.purposeType === FlagPurposeType.additionalReview ||
    PACKAGE_RESTRICTED_ACTIONS_QUEUE_STATUSES.has(queue?.status);

  const availableFlagStatuses = Object.values(FlagStatus).filter((status) =>
    canDeleteFlag ? status : status !== FlagStatus.closedDeleted,
  );
  const flagStatusOptions = availableFlagStatuses.map((status) => ({
    label: FlagStatusLabels[status],
    value: status,
  }));

  const MAX_SUMMARY_LENGTH = 80;
  const problemsSummary = flag?.problems?.map((p: any) => p.displayText)?.join(', ') ?? '';
  const truncatedProblemSummary =
    problemsSummary.length > MAX_SUMMARY_LENGTH
      ? problemsSummary.slice(0, MAX_SUMMARY_LENGTH - 3).trimEnd() + '...'
      : problemsSummary;
  const finalProblemSummary = truncatedProblemSummary?.trim()
    ? truncatedProblemSummary
    : flag?.reason?.trim()
      ? flag.reason
      : 'Unknown flag problems';
  const categoryLabel = setting?.baseCategory ?? 'Unknown flag category';
  const currentPurposeType = flag?.purposeType as FlagPurposeType;
  const openFlagsInGroup = useMemo(
    () =>
      queue?.flags?.filter(
        (flag: any) => flag.purposeType === currentPurposeType && flag.flagType && flag.status === 'open',
      ) ?? [],
    [queue?.flags, currentPurposeType],
  );

  const openFlagsWithoutActionTaken = openFlagsInGroup.filter(
    (flag: Flag) => !flag.actionHistory?.find((action: FlagActionHistory) => action.flagSubject),
  );

  const totalNumberOfFlags = openFlagsInGroup.length ?? 0;
  const totalNumberOfFlagsWithoutActionTaken = openFlagsWithoutActionTaken.length ?? 0;
  const currentFlagIndex = openFlagsInGroup.findIndex((flagItem: any) => flagItem.id === flag.id);
  const displayIndex = currentFlagIndex !== -1 ? currentFlagIndex + 1 : '?';

  const specialFlagDataKeys = Object.values(SpecialFlagSections);
  const isSpecialFlagSection = flag?.dataKey && specialFlagDataKeys?.includes(flag.dataKey);

  const acceptanceCriteriaId = flag?.problems?.[0]?.acceptanceCriteriaId?.toString();
  const matchedCriteria = setting?.acceptanceCriteria?.find(
    (criteria) => criteria.id?.toString?.() === acceptanceCriteriaId,
  );

  const nextFlagIdAvailable = queue?.flags?.find(
    (f: Flag) =>
      !visitedFlags.has(String(f.id)) &&
      f.id !== flagId &&
      f.status === FlagStatus.open &&
      f.flagType &&
      f.purposeType === flag.purposeType,
  )?.id;

  const handleSkip = useCallback(
    (backActionTriggered?: boolean) => {
      if (!openFlagsInGroup.length) {
        return;
      }
      const currentIndex = openFlagsInGroup.findIndex((flagItem: Flag) => flagItem.id === flag.id);
      let index = backActionTriggered ? currentIndex - 1 : currentIndex + 1;
      if (index >= openFlagsInGroup.length) {
        index = 0;
      }
      if (index < 0) {
        index = openFlagsInGroup.length - 1;
      }
      const navFlag = openFlagsInGroup[index];
      if (!navFlag) {
        return;
      }
      setNewNote('');
      setSelectedFlagStatus(FlagStatus.open);
      setChangesOnFlag(false);
      setReplacementFileUpload(undefined);
      setEpcFeedbackFileUpload(undefined);
      setReplacementValue(undefined);
      setInitialValue(undefined);
      setFlagFiles(undefined);
      setFlagIndexKey((prev) => prev + 1);
      isInitialRender.current = true;
      navigate(`/accounts/${account?.id}/${milestonePackage}/flags/${navFlag.id}${from ? '/' + from : ''}`);
    },
    [openFlagsInGroup, flag?.id, account?.id, milestonePackage, from, navigate],
  );

  const handleImmediateUpload = useCallback(
    async (event: any) => {
      const flagFiles: FileList | undefined = event.target.files;
      if (!flagFiles || !flagFiles.length || !account?.id) return;

      const data = new FormData();
      data.append('type', documentType);
      if (from) {
        const fromQueueType = MilestoneRouteToQueueTypeMap[from as MilestonePackageRoute];
        data.append('context', fromQueueType);
      } else {
        data.append('context', queueType);
      }

      for (let i = 0; i < flagFiles.length; i++) {
        data.append(`files[${i}]`, flagFiles[i]);
      }

      try {
        const uploadedDocumentResult = (await uploadDocumentsWithToast({
          uploadDocument,
          accountId: account.id,
          formData: data,
          invalidateAllTags: false,
          files: flagFiles,
        })) as any;
        const uploadedDocumentId = uploadedDocumentResult?.documents?.[0]?.id;
        if (uploadedDocumentId) setReplacementFileUpload(uploadedDocumentId);
      } catch {
        // Toast already shown by helper.
      }
    },
    [account?.id, documentType, from, uploadDocument, queueType],
  );

  const handleImmediateUploadEvidence = useCallback(
    async (event: any) => {
      const flagFiles: FileList | undefined = event.target.files;
      if (!flagFiles || !flagFiles.length || !account?.id) return;

      const data = new FormData();
      data.append('type', epcFeedbackDocumentType);

      for (let i = 0; i < flagFiles.length; i++) {
        data.append(`files[${i}]`, flagFiles[i]);
      }

      try {
        const uploadedDocumentResult = (await uploadDocumentsWithToast({
          uploadDocument,
          accountId: account.id,
          formData: data,
          invalidateAllTags: false,
          files: flagFiles,
        })) as any;
        const postReviewFileUpload = uploadedDocumentResult?.documents?.[0]?.id;

        const flagUpdates: Partial<Flag> = {
          dataKey: flag?.dataKey,
          action: {
            flagSubject: FlagSubject.epcFeedback,
            actionStatus: FlagHistoryStatus.modified,
          },
          postReviewNote: 'EPC Feedback',
          ...(postReviewFileUpload ? { postReviewFileUpload } : {}),
        };

        await updateCurrentFlag({
          id: queue?.id,
          flagId,
          flag: flagUpdates,
        }).unwrap();
        if (postReviewFileUpload) setEpcFeedbackFileUpload(postReviewFileUpload);
      } catch (e) {
        if (isErrorWithData(e)) {
          toast.error(e.data.message);
        }
        // Upload errors already surfaced by helper.
      }
    },
    [account?.id, epcFeedbackDocumentType, flag?.dataKey, flagId, queue?.id, updateCurrentFlag, uploadDocument],
  );

  const handleSaveReviewPackage = useCallback(
    async (values: FormikValues) => {
      const installerRequired = account?.address?.state === 'IL';
      const savePackage = formatReviewData(values, installerRequired) as SavePackageType;

      try {
        if (id) {
          await triggerReviewPackageSave({
            accountId: id,
            savePackage: {
              ...savePackage,
              bypassApprovalValidation: from !== undefined,
            },
          }).unwrap();
          toast.success('Review package saved');
          if (textInputDataProperty) {
            setReplacementValue(textInputDataProperty.value);
          }
        }
      } catch (e) {
        if (isErrorWithData(e)) {
          const errorMessage = e.data.message;
          toast.error(errorMessage);
        } else {
          toast.error('Error saving review package');
        }
      }
    },
    [account?.address?.state, formatReviewData, id, triggerReviewPackageSave, textInputDataProperty, from],
  );

  const handleSaveFlag = useCallback(async () => {
    const flagSubject = hasNoAction
      ? FlagSubject.none
      : needsFormField
        ? FlagSubject.dataInput
        : FlagSubject.documentUpload;

    try {
      let postReviewFileUpload;
      if (flagFiles && flagFiles.length && account?.id) {
        const data = new FormData();
        data.append('type', FlagDocumentTypes.flag);

        for (let i = 0; i < flagFiles.length; i++) {
          data.append(`files[${i}]`, flagFiles[i]);
        }

        const uploadedDocumentResult = (await uploadDocumentsWithToast({
          uploadDocument,
          accountId: account.id,
          formData: data,
          invalidateAllTags: true,
          files: flagFiles,
        })) as any;
        postReviewFileUpload = uploadedDocumentResult?.documents?.[0]?.id;
      }

      const isResolvingFlag = selectedFlagStatus && selectedFlagStatus !== FlagStatus.open;

      const flagUpdates: Partial<Flag> = {
        dataKey: flag?.dataKey,
        action: {
          flagSubject,
          actionStatus: isResolvingFlag
            ? FlagHistoryStatus.resolved
            : replacementFileUpload || replacementValue
              ? FlagHistoryStatus.issueAddressed
              : isReviewer
                ? FlagHistoryStatus.qcFeedback
                : FlagHistoryStatus.modified,
          ...(replacementFileUpload
            ? {
                flaggedDocumentId: flaggedDocument?.id ? flaggedDocument.id.toString() : 'none',
                replacementDocumentId: replacementFileUpload,
              }
            : {}),
          ...(replacementValue && initialValue
            ? {
                changedFromReviewPackageValue: initialValue,
                changedToReviewPackageValue: replacementValue,
              }
            : {}),
        },
        ...(newNote ? { postReviewNote: newNote } : {}),
        ...(postReviewFileUpload ? { postReviewFileUpload } : {}),
        ...(isResolvingFlag ? { status: selectedFlagStatus, resolved: true } : {}),
      };

      await updateCurrentFlag({
        id: queue?.id,
        flagId,
        flag: flagUpdates,
      }).unwrap();
      toast.success('Flag saved');
      handleSkip(); //TODO: This feature produces a weird effect on flag saving. Need a transition or something.
    } catch (e) {
      if (isErrorWithData(e)) {
        const errorMessage = e.data.message;
        toast.error(errorMessage);
      } else {
        toast.error('Error saving flag');
      }
    }
  }, [
    hasNoAction,
    needsFormField,
    flagFiles,
    selectedFlagStatus,
    flag?.dataKey,
    replacementFileUpload,
    replacementValue,
    flaggedDocument,
    initialValue,
    newNote,
    isReviewer,
    updateCurrentFlag,
    queue?.id,
    flagId,
    handleSkip,
    uploadDocument,
    account?.id,
  ]);

  const handleDoneClick = useCallback(() => {
    navigate(
      `/accounts/${account?.id}/${from ? from : milestonePackage}/${action ? action : isReviewer ? 'review' : 'edit'}`,
    );
  }, [navigate, account?.id, from, milestonePackage, action, isReviewer]);

  const { showNewMessage } = useIntercom();

  useEffect(() => {
    if (flagId) {
      setVisitedFlags((prev) => new Set(prev).add(flagId));
    }
  }, [flagId, nextFlagIdAvailable]);

  useEffect(() => {
    if ((flag?.status && flag?.status !== selectedFlagStatus) || (newNote && newNote !== flag?.note)) {
      setChangesOnFlag(true);
    }
  }, [flag?.note, flag?.status, newNote, selectedFlagStatus]);

  return (
    <QueueFlagsProvider queueId={queue?.id}>
      <NewAddFlagModal
        accountId={queue?.account?.id}
        isOpen={isOpen}
        onDismiss={handleClose}
        dataKey={flag?.dataKey}
        fileName={flag?.fileName}
        baseCategory={flag?.baseCategory}
        flagType={flag?.flagType}
        type={flag?.type}
        showUploadButton={isReviewer}
        editMode={true}
        flagId={flag?.id}
        queueStatus={queue?.status}
      />
      <MainContainer>
        <PageHeader
          eyebrow={
            <>
              <Link to="/accounts">Accounts</Link> \{' '}
              <Link to={`/accounts/${account?.id}`}>{`${account?.primaryApplicantName}`}</Link> \{' '}
              <Link
                to={`/accounts/${account?.id}/${from ? from : milestonePackage}/${action ? action : isReviewer ? 'review' : 'edit'}`}
              >
                {MilestonePackageRouteToLabels[(from ? from : milestonePackage) as MilestonePackageRoute]}
              </Link>{' '}
              \{` ${flagIsClosed ? 'Flag Closed' : 'Action Required'}`}
            </>
          }
          title={`${account?.primaryApplicantName}`}
        />
        <Box
          padding={{ base: '0 lg', desktop: 'md 0' }}
          direction={{ base: 'column', tablet: 'row' }}
          justifyContent={{ base: 'flex-start', tablet: 'space-between' }}
          childGap={{ base: 'md', tablet: '0' }}
        >
          <Box fontWeight="medium" fontSize="lg">
            {categoryLabel && categoryLabel !== flagLabel ? categoryLabel + ' / ' + flagLabel : flagLabel}
          </Box>
          <Box
            width={badgeTextValuesAccordingToChangesType.changes ? '2xl' : 'xl'}
            direction="column"
            alignItems="flex-start"
            childGap="xs"
          >
            <Badge
              variant={badgeTextValuesAccordingToChangesType.changes ? 'success' : 'danger'}
              message={badgeTextValuesAccordingToChangesType.text}
              size="sm"
              align="center"
            />
            {changesMadeOnFlag.date && (
              <DateTimeDisplay value={changesMadeOnFlag.date} fontSize="xs" dateFormat="DD h:mm:ss a" inline />
            )}
          </Box>
        </Box>
        <Box
          direction={{ base: 'column', desktop: 'row' }}
          width="100"
          radius="md"
          padding={{ base: '0 lg', desktop: '0' }}
          margin="0 0 xl 0"
        >
          <FlagStatusDisplay
            flag={flag}
            currentPurposeType={currentPurposeType}
            isReviewer={isReviewer}
            handleOpen={handleOpen}
            matchedCriteria={matchedCriteria}
            flagLabel={flagLabel}
          />
          <Box width="100">
            <Box padding={{ base: 'xl', desktop: '0' }} childGap="lg">
              <Box
                display="flex"
                direction="row"
                alignItems="flex-start"
                gap="md"
                borderWidth="0 0 0 sm"
                style={{ marginBottom: '-2px', paddingBottom: '6em' }}
              >
                <svg
                  viewBox="0 0 25 25"
                  height="1.15em"
                  width="1.15em"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ marginLeft: '-8px', marginTop: '-1px' }}
                >
                  <g strokeWidth="2" fill="currentColor" stroke="currentColor">
                    <circle
                      cx="12"
                      cy="12"
                      r="11"
                      fill="currentColor"
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      data-cap="butt"
                    ></circle>
                  </g>
                </svg>
                <Box width="100">
                  <Box
                    direction={{ base: 'column', tablet: 'row' }}
                    display="flex"
                    justifyContent="space-between"
                    alignItems={needsFormField ? 'center' : 'flex-start'}
                    gap="lg"
                  >
                    {isReviewer && !flagIsClosed && (
                      <Box width={{ base: '3xl', desktop: '30' }}>
                        <SelectInputInset
                          id="flag-status"
                          label="Change Flag Status"
                          options={flagStatusOptions}
                          value={selectedFlagStatus}
                          size="lg"
                          onChange={(newVal) => {
                            setSelectedFlagStatus(newVal.target.value);
                          }}
                          disabled={isAccountLoading || isQueueLoading}
                        />
                      </Box>
                    )}
                    {flagIsClosed && (
                      <Box width={{ base: '3xl', desktop: '30' }} fontWeight="bold" fontSize="lg">
                        {FlagStatusLabels[flag?.status as FlagStatus]}
                      </Box>
                    )}

                    <FlagInteractionControls
                      hasNoAction={hasNoAction}
                      needsFormField={needsFormField}
                      isSpecialFlagSection={isSpecialFlagSection}
                      flagIsClosed={flagIsClosed}
                      isReviewer={isReviewer}
                      documentType={documentType}
                      epcFeedbackDocumentType={epcFeedbackDocumentType}
                      handleImmediateUpload={handleImmediateUpload}
                      handleImmediateUploadEvidence={handleImmediateUploadEvidence}
                      showNewMessage={showNewMessage}
                      isAccountLoading={isAccountLoading}
                      isQueueLoading={isQueueLoading}
                      isDocumentsLoading={isDocumentsLoading}
                      isUploadingDocument={isUploadingDocument}
                    />
                  </Box>

                  {replacementFileUpload && (
                    <Box padding="sm 0" color="success" fontWeight="bold">
                      Upload was successful. Click &quot;Next&quot; to proceed.
                    </Box>
                  )}

                  {needsFormField && !flagIsClosed && (
                    <ReviewPackageForm
                      initialValues={initialValues}
                      onSubmit={handleSaveReviewPackage}
                      textInputDataProperty={textInputDataProperty}
                      flagLabel={flagLabel}
                      isAccountLoading={isAccountLoading}
                      isQueueLoading={isQueueLoading}
                    />
                  )}
                  {isReviewer && !flagIsClosed && (
                    <ReviewerQCSection
                      newNote={newNote}
                      onNoteChange={setNewNote}
                      flagFiles={flagFiles}
                      onFilesChange={saveFilesForFutureUpload}
                      flagIndexKey={flagIndexKey}
                      isPhone={isPhone}
                      isAccountLoading={isAccountLoading}
                      isQueueLoading={isQueueLoading}
                      isUploadingDocument={isUploadingDocument}
                    />
                  )}
                </Box>
              </Box>
              <FlagActionTimeline
                sortedActionHistory={sortedActionHistory}
                flag={flag}
                documents={documents}
                flaggedDocumentWithSingleFile={flaggedDocumentWithSingleFile}
                queueStatusIsApprovedOrConditionallyApproved={queueStatusIsApprovedOrConditionallyApproved}
                isSpecialFlagSection={isSpecialFlagSection}
              />
            </Box>
          </Box>
          <TasksFooter
            displayIndex={displayIndex}
            totalNumberOfFlags={totalNumberOfFlags}
            totalNumberOfFlagsWithoutActionTaken={totalNumberOfFlagsWithoutActionTaken}
            categoryLabel={categoryLabel}
            problemsSummary={problemsSummary}
            finalProblemSummary={finalProblemSummary}
            flag={flag}
            savingFlagLoading={isLoading}
            changesOnFlag={
              changesOnFlag ||
              Boolean(replacementValue) ||
              Boolean(replacementFileUpload) ||
              Boolean(epcFeedbackFileUpload)
            }
            onSkipClick={handleSkip}
            handleSave={handleSaveFlag}
            onDoneClick={handleDoneClick}
            isReviewer={isReviewer}
          />
        </Box>
      </MainContainer>
    </QueueFlagsProvider>
  );
};
